import { addDays, addMinutes, addMonths, addWeeks, differenceInCalendarMonths, differenceInMinutes, endOfDay, format, formatISO, getDate, getMonth, getWeekOfMonth, isWeekend, parse, parseISO, setDay, startOfDay, startOfMonth, startOfWeek, } from "date-fns";
import { enUS } from "date-fns/locale";
import { uid } from "./uid";
import { getDeepCopy, isDeepEqual } from "./helpers";
import { isSameDateTime, setTimeFromDate } from "./time";
// default object
const defaultRRule = {
    freq: "NEVER",
    FREQ: "DAILY",
    INTERVAL: 1,
    BYDAY: [],
    BYMONTH: 1,
    BYMONTHDAY: 1,
    UNTIL: null,
    COUNT: 1,
    EXDATE: [],
    ends: "NEVER",
    byset: "DAY",
};
export function icalFormat(date) {
    return formatISO(date).split("+")[0];
}
export function dateFromIcal(str) {
    return parseISO(str);
}
// Removes unnecessary parts of pattern according to rule
export function normalizeRRULEObj(config) {
    const { weekDays, freq, ends, EXDATE, COUNT, UNTIL, byset, ...rest } = config;
    config = rest;
    let normalize = {};
    switch (freq) {
        case "CUSTOM": {
            for (const key in config) {
                const value = config[key];
                if (!value)
                    continue;
                normalize[key] = value;
            }
            break;
        }
        case "WORKDAYS": {
            const date = new Date();
            const workdays = weekDays
                .filter(d => !isWeekend(parse(d.id, "EEEEEE", date)))
                .map(d => d.id);
            normalize = { INTERVAL: 1, BYDAY: workdays, FREQ: "WEEKLY" };
            break;
        }
        case "NEVER":
            break;
        default:
            normalize.FREQ = freq;
    }
    switch (normalize.FREQ) {
        case "WEEKLY": {
            const { BYMONTHDAY, BYSETPOS, BYMONTH, BYPOS, ...rest } = normalize;
            normalize = { ...rest };
            break;
        }
        case "DAILY": {
            const { BYDAY, BYSETPOS, BYMONTH, BYMONTHDAY, BYPOS, ...rest } = normalize;
            normalize = { ...rest };
            break;
        }
        case "MONTHLY": {
            const { BYDAY, BYSETPOS, BYMONTH, BYMONTHDAY, ...rest } = normalize;
            normalize = { ...rest };
            if (byset === "DAY") {
                normalize = { ...normalize, BYMONTHDAY };
            }
            else {
                normalize = { ...normalize, BYSETPOS, BYDAY };
            }
            break;
        }
        case "YEARLY": {
            const { BYDAY, BYMONTHDAY, BYSETPOS, ...rest } = normalize;
            normalize = { ...rest };
            if (byset === "DAY") {
                normalize = { ...normalize, BYMONTHDAY };
            }
            else {
                normalize = { ...normalize, BYSETPOS, BYDAY };
            }
            break;
        }
    }
    switch (ends) {
        case "ON": {
            normalize = {
                ...normalize,
                UNTIL: icalFormat(endOfDay(UNTIL)),
            };
            break;
        }
        case "AFTER": {
            normalize = { ...normalize, COUNT };
            break;
        }
    }
    if (EXDATE.length) {
        normalize.EXDATE = EXDATE.map(d => d && icalFormat(d)).join(",");
    }
    return normalize;
}
// Converts  IRRULEObj to string
export function serializeRecurring(config) {
    const cnf = normalizeRRULEObj(config);
    const str = [];
    for (const key in cnf) {
        const value = cnf[key];
        if (value)
            str.push(`${key}=${value}`);
    }
    return str.join(";");
}
export function getRRULEConstByDate(date) {
    const BYDAY = [format(date, "EEEEEE").toUpperCase()];
    const BYMONTH = getMonth(date) + 1;
    const BYMONTHDAY = +getDate(date);
    const BYSETPOS = +getWeekOfMonth(date);
    return { BYDAY, BYMONTH, BYMONTHDAY, BYSETPOS };
}
//Converts string to IRRULEObj
export function parseRecurring(string, event, _dates = enUS, includeDefaults = true) {
    if (typeof string === "object" && string)
        return string;
    const { start_date, end_date, STDATE } = event;
    const { BYDAY, BYMONTH, BYMONTHDAY, BYSETPOS } = getRRULEConstByDate(STDATE || start_date);
    const config = includeDefaults
        ? {
            ...defaultRRule,
            weekDays: getWeekDays(_dates),
            BYDAY,
            BYMONTH,
            BYSETPOS,
            BYMONTHDAY,
            UNTIL: startOfDay(addDays(end_date, 1)),
        }
        : { weekDays: getWeekDays(_dates) };
    if (!string) {
        return config;
    }
    //FREQ=DAILY;INTERVAL=2
    const parts = string.split(";");
    for (let i = 0; i < parts.length; i++) {
        const splited = parts[i].split("=");
        const code = splited[0];
        let name = splited[1];
        if (code === "UNTIL") {
            name = dateFromIcal(name);
            config.ends = "ON";
        }
        if (code === "EXDATE") {
            name = name.split(",").map((d) => dateFromIcal(d));
        }
        if (code === "BYDAY") {
            name = name.split(",").map((d) => d.toUpperCase());
        }
        if (code === "COUNT") {
            config.ends = "AFTER";
        }
        if (code === "BYSETPOS") {
            config.byset = "POS";
        }
        const numeric = parseInt(name);
        config[code] = !isNaN(numeric) ? numeric : name;
    }
    if (parts.length > 1 && !parts[1].startsWith("EXDATE")) {
        config.freq = "CUSTOM";
    }
    else if (parts[0].startsWith("FREQ")) {
        config.freq = config.FREQ;
    }
    return config;
}
export function getWeekDays(locale = enUS) {
    const weekDays = [];
    let weekStart = startOfWeek(new Date(), { locale });
    for (let i = 0; i < 7; i++) {
        const name = format(weekStart, "EEEEEE", { locale });
        const id = format(weekStart, "EEEEEE", {
            weekStartsOn: locale.options.weekStartsOn,
        }).toUpperCase();
        const fullName = format(weekStart, "EEEE", { locale });
        weekDays.push({ id, name, index: i, fullName });
        weekStart = addDays(weekStart, 1);
    }
    return weekDays;
}
// Returns the last date of pattern
export function getLastDate(rule, event) {
    const start = event.STDATE || event.start_date;
    const { COUNT, UNTIL, ends } = rule;
    switch (ends) {
        case "ON":
            return UNTIL;
        case "AFTER": {
            let currentDate = new Date(start);
            const wdays = fillWdays(rule);
            for (let i = 1; i < COUNT; i++) {
                currentDate = next(currentDate, rule, wdays);
            }
            return currentDate;
        }
        default:
            return new Date(Date.UTC(9999, 11, 31, 0, 0));
    }
}
export function next(date, mode, wdays) {
    const { FREQ, INTERVAL = 1, BYMONTHDAY, BYSETPOS, BYDAY, weekDays } = mode;
    let interval = INTERVAL * 1;
    if (FREQ == "YEARLY")
        interval *= 12;
    let endDate = new Date(date);
    let now, step;
    switch (FREQ) {
        case "DAILY":
            endDate = addDays(endDate, interval);
            break;
        case "WEEKLY":
            now = endDate.getDay();
            step = wdays ? wdays[now] : 7;
            if (step + now > 6 && interval > 1) {
                // when new day is on the different week
                // add week interval as well
                step += 7 * (interval - 1);
            }
            endDate = addDays(endDate, step);
            break;
        case "YEARLY":
        case "MONTHLY":
            now = endDate.getMonth();
            endDate = addMonths(endDate, interval);
            if (BYMONTHDAY) {
                endDate.setDate(BYMONTHDAY);
                // when date is outside of month bounds, switch to last day of month
                if ((now + interval) % 12 != endDate.getMonth())
                    endDate.setDate(0);
            }
            else if (BYSETPOS) {
                // go to first day of month;
                endDate = startOfMonth(endDate);
                // set week
                endDate = addWeeks(endDate, +BYSETPOS - 1);
                const targetDay = weekDays.find(({ id }) => BYDAY[0] === id).index;
                let nextDate = setDay(endDate, targetDay);
                const monthDiff = differenceInCalendarMonths(endDate, nextDate);
                if (monthDiff) {
                    nextDate = setDay(addWeeks(nextDate, monthDiff), targetDay);
                }
                endDate = nextDate;
                // set BYDAY
            }
            break;
    }
    return endDate;
}
export function fillWdays(mode) {
    const wdays = [0, 0, 0, 0, 0, 0, 0];
    const { BYDAY, weekDays } = mode;
    if (BYDAY) {
        const diff = wdays;
        const line = BYDAY.map(a => weekDays.findIndex(i => i.id === a)).sort();
        // for each allowed day, fill range with steps
        for (let i = 0; i < line.length; i++) {
            const s = line[i];
            const e = line[i + 1] || line[0] + 7;
            for (let j = s; j < e; j++) {
                diff[j % 7] = e - j;
            }
        }
        return diff;
    }
}
// Converts recurring event into array of events
export function explodeEvent(sdate, edate, parent, children = [], _dates = enUS) {
    if (!parent.RRULE && !parent.recurring)
        return [parent];
    const mode = parseRecurring(parent.RRULE, parent, _dates, false);
    const { COUNT, UNTIL, FREQ, BYDAY } = mode;
    let { EXDATE = [] } = mode;
    if (children.length) {
        const exdates = children.map(e => e.originalStartTime);
        EXDATE = [...EXDATE, ...exdates];
    }
    const until = UNTIL?.valueOf();
    let { start_date, end_date } = parent;
    const { id } = parent;
    const res = [];
    // when we use week-days in recurring mode, convert structure
    // list of days => list of steps to the next allowed day
    const wdays = fillWdays(mode);
    // when we have work BYDAY rule, we need to adjust start date
    if (FREQ === "WEEKLY" && BYDAY) {
        const id = format(start_date, "EEEEEE", {
            weekStartsOn: _dates.options.weekStartsOn,
        }).toUpperCase();
        // readjust start date to the first allowed day
        if (!BYDAY.includes(id)) {
            start_date = next(start_date, mode, wdays);
            end_date = next(end_date, mode, wdays);
        }
    }
    let xdate = new Date(start_date);
    let xdateValue = xdate.valueOf();
    const length = end_date.valueOf() - xdateValue;
    // occurrences before active window
    let count = 1;
    while ((sdate.valueOf() - xdateValue >= (length || 1) &&
        (!UNTIL || xdateValue < until) &&
        (!COUNT || count <= COUNT)) ||
        findDate(EXDATE, xdate)) {
        ++count;
        xdate = next(xdate, mode, wdays);
        xdateValue = xdate.valueOf();
    }
    // occurrences inside of window
    while (edate.valueOf() > xdateValue &&
        (!UNTIL || xdateValue < until) &&
        (!COUNT || count <= COUNT)) {
        // make copy of recurring event
        if (!findDate(EXDATE, xdate)) {
            const copy = getDeepCopy(parent);
            const x = new Date(xdate).setHours(xdate.getHours());
            copy.start_date = new Date(x);
            copy.end_date = new Date(x * 1 + length);
            if (!isDeepEqual(copy, parent)) {
                copy.recurringEventId = id;
                copy.id = copy.start_date.valueOf() + "wx_recurring" + id;
            }
            res.push(copy);
        }
        count++;
        xdate = next(xdate, mode, wdays);
        xdateValue = xdate.valueOf();
    }
    return res;
}
export function findDate(arr, date) {
    if (!arr)
        return false;
    const res = arr.find(d => isSameDateTime(d, date));
    return !!res;
}
export function getRecurringById(id, events) {
    const [date, recurringEventId] = `${id}`.split("wx_recurring");
    let parent;
    const children = [];
    events.forEach(e => {
        if (e.id == recurringEventId) {
            parent = e;
        }
        if (e.recurringEventId == id) {
            children.push(e);
        }
    });
    const event = explodeEvent(startOfDay(+date), endOfDay(+date), parent, children);
    // multiple instances may overlap the selected date
    // make sure to select the one with matching id
    return event.find(e => e.id === id);
}
export function updateRecurring(event, initEvent, originalEvent, action, mode, dispatch) {
    const actionData = { action };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { recurringEventId: rid, id, start_date, end_date, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    originalStartTime: o, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    start_date_time: sdt, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    end_date_time: edt, ...updatedFields } = event;
    const recurringEventId = rid || id;
    const { start_date: sd, end_date: ed, originalStartTime } = initEvent || {};
    // change mode to "all" if event is parent
    if (mode === "future" && sd.valueOf() === initEvent.STDATE?.valueOf()) {
        mode = "all";
    }
    let newEvent;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { start_date: _s, end_date: _e } = event;
    switch (mode) {
        case "all": {
            const newParent = {
                ...originalEvent,
                ...updatedFields,
                start_date,
                end_date,
                recurring: true,
                STDATE: start_date,
                DTEND: updatedFields.DTEND || originalEvent.DTEND,
                RRULE: updatedFields.RRULE || originalEvent.RRULE,
            };
            if (action === "delete-event") {
                return dispatch("action", {
                    action,
                    data: {
                        id: recurringEventId,
                    },
                });
            }
            // if it's not a parent event and dates are changed
            if (!recurringEventId &&
                (start_date.valueOf() !== sd.valueOf() ||
                    ed.valueOf() !== end_date.valueOf())) {
                const seriesStart = originalEvent.STDATE;
                // update parent dates
                newParent.start_date = setTimeFromDate(seriesStart, start_date);
                newParent.end_date = addMinutes(new Date(newParent.start_date), differenceInMinutes(end_date, start_date));
                // update rrule start date
                newParent.STDATE = newParent.start_date;
            }
            // set recurringEventId as main id
            newParent.id = recurringEventId;
            actionData.data = {
                id: recurringEventId,
                event: newParent,
                mode: "all",
                recurringEventId,
            };
            dispatch("action", actionData);
            break;
        }
        case "only": {
            // if it's already EXDATE from rule than we need to update only this event
            if (originalStartTime) {
                newEvent = { ...event };
                if (action === "delete-event") {
                    newEvent.status = "cancelled";
                }
                dispatch("action", {
                    action: "update-event",
                    data: {
                        id: event.id,
                        event: newEvent,
                    },
                });
                return;
            }
            const RRULEObj = parseRecurring(initEvent.RRULE, event);
            const prevEXDATE = RRULEObj?.EXDATE || [];
            const EXDATE = [...prevEXDATE];
            const newRRULE = RRULEObj
                ? serializeRecurring({ ...RRULEObj, EXDATE })
                : null;
            // generate new rule
            const newParentEvent = {
                ...originalEvent,
                RRULE: newRRULE,
            };
            const data = {
                id: recurringEventId,
                event: newParentEvent,
                recurringEventId,
            };
            dispatch("action", { action: "update-event", data });
            newEvent = {
                ...event,
                recurringEventId,
                originalStartTime: new Date(sd),
                id: uid(),
            };
            if (action === "delete-event") {
                delete newEvent.start_date;
                delete newEvent.end_date;
                newEvent.status = "cancelled";
            }
            dispatch("action", {
                action: "add-event",
                data: {
                    event: cleanEventFromRecurring(newEvent),
                },
            });
            if (action === "update-event" && event.recurring) {
                dispatch("action", {
                    action: "select-event",
                    data: {
                        id: newEvent.id,
                        popup: false,
                    },
                });
            }
            break;
        }
        case "future": {
            const RRULEObj = parseRecurring(originalEvent.RRULE, originalEvent);
            const UNTIL = addDays(sd, -1);
            const newRRULE = serializeRecurring({
                ...RRULEObj,
                UNTIL: UNTIL,
                ends: "ON",
            });
            // generate new rule
            const newParentEvent = {
                ...originalEvent,
                RRULE: newRRULE,
                DTEND: endOfDay(UNTIL),
            };
            const data = {
                id: recurringEventId,
                event: newParentEvent,
                recurringEventId,
            };
            dispatch("action", { action: "update-event", data });
            if (action === "update-event") {
                // add new event
                const { recurringEventId, RRULE, DTEND, ...rest } = event;
                newEvent = {
                    ...originalEvent,
                    ...rest,
                    recurring: true,
                    id: uid(),
                    RRULE: RRULE || originalEvent.RRULE,
                    DTEND: DTEND || originalEvent.DTEND,
                    STDATE: start_date,
                };
                dispatch("action", {
                    action: "add-event",
                    data: { event: newEvent, mode: "future", recurringEventId },
                });
                dispatch("action", {
                    action: "select-event",
                    data: {
                        id: newEvent.id,
                        popup: false,
                    },
                });
                return newEvent.id;
            }
            break;
        }
    }
}
export function postDragRecurring({ event, initEvent, }) {
    if (isSameDateTime(event.start_date, initEvent.start_date) &&
        isSameDateTime(event.end_date, initEvent.end_date)) {
        return event;
    }
    const RRULEObj = parseRecurring(initEvent.RRULE, event);
    const { FREQ, BYDAY } = RRULEObj;
    if (FREQ !== "WEEKLY" || BYDAY?.length !== 1)
        return event;
    const currentBYDAY = format(event.start_date, "EEEEEE").toUpperCase();
    if (currentBYDAY === BYDAY[0])
        return event;
    const newRRULE = serializeRecurring({ ...RRULEObj, BYDAY: [currentBYDAY] });
    event.RRULE = newRRULE;
    return event;
}
export function cleanEventFromRecurring(event) {
    const { RRULE, DTEND, EXDATE, STDATE, ...rest } = event;
    return { ...rest, recurring: false };
}
//# sourceMappingURL=recurring.js.map